"use client";

import { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Treatment } from "@/lib/treatments-data";
import Header from "@/components/layout/header";

import Breadcrumbs from "@/components/Breadcrumbs";
import VeneersHeroSection from "./dental-veneers/hero-section";
import WhatAreVeneersSection from "./dental-veneers/what-are-section";
import TypesOfVeneersSection from "./dental-veneers/types-section";
import TreatmentJourneySection from "./dental-veneers/journey-section";
import PricingInvestmentSection from "./dental-veneers/pricing-investment-section";
import LongevityCareSection from "./dental-veneers/longevity-section";
import PermanenceSection from "./dental-veneers/permanence-section";
import QualificationSection from "./dental-veneers/qualification-section";
import GallerySection from "./dental-veneers/gallery-section";
import FinalCTASection from "./shared/final-cta-section";
import StickyNavigation from "./shared/sticky-navigation";
import { ArrowLeft } from "lucide-react";
import Link from "next/link";
import { useTranslation } from "@/hooks/useTranslation";

gsap.registerPlugin(ScrollTrigger);

interface DentalVeneersPageProps {
  treatment: Treatment;
}

export default function DentalVeneersPage({
  treatment,
}: DentalVeneersPageProps) {
  const { t, locale } = useTranslation('dental_veneers');
  const pageRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Initialize GSAP ScrollTrigger
    ScrollTrigger.refresh();

    return () => {
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
    };
  }, []);

  const sections = [
    { id: "hero", label: t('navigation.sections.home') },
    { id: "what-are", label: t('navigation.sections.whatAre') },
    { id: "types", label: t('navigation.sections.types') },
    { id: "journey", label: t('navigation.sections.process') },
    { id: "qualification", label: t('navigation.sections.candidates') },
    { id: "pricing", label: t('navigation.sections.pricing') },
    { id: "permanence", label: t('navigation.sections.permanence') },
    { id: "longevity", label: t('navigation.sections.care') },
    { id: "gallery", label: t('navigation.sections.gallery') },
  ];

  return (
    <div ref={pageRef} className="min-h-screen bg-slate-50 dark:bg-navy-950 overflow-x-clip">
      <Header />

      {/* Treatment Header Section */}
      <section className="relative bg-gradient-to-b from-navy to-navy-950 border-b border-white/10 pt-20 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_center,rgba(210,161,56,0.08)_0%,transparent_60%)]" />
        <div className="container relative py-6">
          {/* Breadcrumbs */}
          <div className="flex items-center justify-between mb-4">
            <Breadcrumbs />
            <Link
              href={`/${locale}/treatments/`}
              className="hidden md:flex items-center space-x-2 text-white/60 hover:text-gold transition-colors text-sm"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>{t('navigation.backToTreatments')}</span>
            </Link>
          </div>
        </div>
      </section>

      <StickyNavigation sections={sections} />

      <main className="relative">
        <VeneersHeroSection treatment={treatment} />
        <WhatAreVeneersSection />
        <TypesOfVeneersSection />
        <TreatmentJourneySection />
        <QualificationSection />
        <PricingInvestmentSection />
        <PermanenceSection />
        <LongevityCareSection />
        <GallerySection />

        {/* Final CTA */}
        <FinalCTASection
          treatmentName={t('finalCta.treatmentName')}
          treatmentKeyword={t('finalCta.treatmentKeyword')}
          scrollToSection="booking"
          customHeading={
            <>
              {t('finalCta.heading')}
            </>
          }
          customSubheading={t('finalCta.subheading')}
          customBenefits={t('finalCta.benefits', { returnObjects: true }) as string[]}
        />
      </main>

    </div>
  );
}
